import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MusicPicker, LocationPicker, MusicTag, LocationTag } from "@/components/MusicLocation";
import { toast } from "sonner";
import { Plus, X, Send, Camera, Trash2, Eye } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import MentionPicker from "@/components/MentionPicker";
import StoryReplyBar from "@/components/StoryReplyBar";
import LinkPreviewCard from "@/components/LinkPreviewCard";
import useLinkPreview from "@/hooks/useLinkPreview";
import { linkify } from "@/components/ExpandableText";
import { ExternalLink, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import ImageUploader from "@/components/ImageUploader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BG_PALETTE = ["#111827", "#7c3aed", "#0ea5e9", "#ef4444", "#f59e0b", "#10b981", "#ec4899"];

export default function StoriesRow() {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [openViewer, setOpenViewer] = useState(null); // {groupIndex, storyIndex}
  const [openComposer, setOpenComposer] = useState(false);
  const [openViewers, setOpenViewers] = useState(null); // story_id
  const [viewersList, setViewersList] = useState([]);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/stories`);
      setGroups(r.data || []);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, []);

  const openGroup = (idxOrId) => {
    const idx = typeof idxOrId === "string" ? groups.findIndex((x) => x.user_id === idxOrId) : idxOrId;
    const g = groups[idx];
    if (!g || (g.stories || []).length === 0) return;
    setOpenViewer({ groupIndex: idx, storyIndex: 0 });
    const s = g.stories[0];
    if (!s.seen && s.story_id) axios.post(`${API}/stories/${s.story_id}/view`).catch(() => {});
  };

  const nextStory = () => {
    if (!openViewer) return;
    const g = groups[openViewer.groupIndex];
    const nextIdx = openViewer.storyIndex + 1;
    if (nextIdx < g.stories.length) {
      const ns = g.stories[nextIdx];
      if (!ns.seen && ns.story_id) axios.post(`${API}/stories/${ns.story_id}/view`).catch(() => {});
      setOpenViewer({ ...openViewer, storyIndex: nextIdx });
    } else {
      // next group
      const nextGroupIdx = openViewer.groupIndex + 1;
      if (nextGroupIdx < groups.length && groups[nextGroupIdx].stories.length > 0) {
        const ng = groups[nextGroupIdx];
        const ns = ng.stories[0];
        if (!ns.seen && ns.story_id) axios.post(`${API}/stories/${ns.story_id}/view`).catch(() => {});
        setOpenViewer({ groupIndex: nextGroupIdx, storyIndex: 0 });
      } else {
        setOpenViewer(null);
        load();
      }
    }
  };
  const prevStory = () => {
    if (!openViewer) return;
    if (openViewer.storyIndex > 0) setOpenViewer({ ...openViewer, storyIndex: openViewer.storyIndex - 1 });
  };

  const openViewersFor = async (story_id) => {
    setOpenViewers(story_id);
    try {
      const r = await axios.get(`${API}/stories/${story_id}/viewers`);
      setViewersList(r.data || []);
    } catch { setViewersList([]); }
  };
  const deleteStory = async (story_id) => {
    if (!window.confirm("Excluir esse story?")) return;
    try {
      await axios.delete(`${API}/stories/${story_id}`);
      toast.success("Story excluído");
      setOpenViewer(null);
      load();
    } catch (e) { toast.error("Erro"); }
  };

  const current = openViewer ? groups[openViewer.groupIndex]?.stories?.[openViewer.storyIndex] : null;
  const currentGroup = openViewer ? groups[openViewer.groupIndex] : null;

  const [replying, setReplying] = useState(false);
  useEffect(() => { setReplying(false); }, [openViewer?.groupIndex, openViewer?.storyIndex]);

  // Auto-advance: 6s for image/text stories; videos advance on end (see onEnded). Paused while typing a reply.
  useEffect(() => {
    if (!openViewer || !current || current.video_url || replying) return;
    const t = setTimeout(nextStory, 6000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openViewer?.groupIndex, openViewer?.storyIndex, current?.story_id, replying]);
  const isOwn = currentGroup?.user_id === user?.user_id;

  return (
    <>
      <div
        data-testid="stories-row"
        className="flex gap-2.5 sm:gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x scrollbar-none"
        style={{ scrollbarWidth: "none" }}
      >
        {/* Self "add story" tile (Facebook-sized) */}
        <button
          data-testid="stories-add-btn"
          onClick={() => setOpenComposer(true)}
          className="flex-shrink-0 w-[112px] sm:w-[130px] snap-start group text-left"
        >
          <div className="relative w-full h-[190px] sm:h-[220px] rounded-2xl overflow-hidden border border-border bg-muted shadow-sm group-hover:shadow-md transition-shadow">
            <div className="h-[70%] w-full overflow-hidden">
              <Avatar className="w-full h-full rounded-none">
                <AvatarImage src={user?.picture} className="object-cover group-hover:scale-105 transition-transform duration-300" />
                <AvatarFallback className="rounded-none text-2xl">{user?.name?.[0] || "?"}</AvatarFallback>
              </Avatar>
            </div>
            <div className="h-[30%] w-full bg-card flex items-end justify-center pb-2">
              <span className="text-[11px] sm:text-xs font-semibold">Criar story</span>
            </div>
            <div className="absolute left-1/2 -translate-x-1/2 top-[70%] -translate-y-1/2 w-9 h-9 rounded-full bg-primary flex items-center justify-center border-4 border-card">
              <Plus className="w-4 h-4 text-primary-foreground" />
            </div>
          </div>
        </button>

        {groups.filter(g => g.user_id !== user?.user_id || g.stories.length > 0).map((g, idx) => (
          <button
            key={g.user_id}
            data-testid={`story-group-${g.user_id}`}
            onClick={() => openGroup(g.user_id)}
            className="flex-shrink-0 w-[112px] sm:w-[130px] snap-start group text-left"
          >
            <div className={`w-full h-[190px] sm:h-[220px] rounded-2xl p-[3px] ${g.has_unseen ? "bg-gradient-to-b from-primary via-pink-500 to-orange-400" : "bg-muted"}`}>
              <div className="w-full h-full rounded-[13px] overflow-hidden relative bg-background">
                {g.stories[0]?.image_url ? (
                  <img src={g.stories[0].image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white text-sm font-bold p-2 text-center" style={{ background: g.stories[0]?.bg_color || "#111827" }}>
                    {(g.stories[0]?.text || "").slice(0, 60)}
                  </div>
                )}
                <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/70 to-transparent" />
                <Avatar className={`absolute top-2 left-2 w-9 h-9 border-[3px] ${g.has_unseen ? "border-primary" : "border-white"}`}>
                  <AvatarImage src={g.author_picture} />
                  <AvatarFallback className="text-[10px]">{g.author_name?.[0]}</AvatarFallback>
                </Avatar>
                {g.stories.length > 1 && (
                  <span className="absolute top-2 right-2 text-[10px] font-bold text-white bg-black/50 rounded-full px-1.5 py-0.5">{g.stories.length}</span>
                )}
                <div className="absolute bottom-2 left-2 right-2 text-white text-[11px] sm:text-xs font-semibold truncate drop-shadow">{g.author_name?.split(" ")[0]}</div>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Composer */}
      <StoryComposer open={openComposer} onOpenChange={setOpenComposer} onCreated={() => { setOpenComposer(false); load(); }} />

      {/* Viewer */}
      <Dialog open={!!openViewer} onOpenChange={(v) => !v && setOpenViewer(null)}>
        <DialogContent className="p-0 max-w-md h-[80vh] overflow-hidden bg-black" data-testid="story-viewer">
          {current && currentGroup && (
            <div className="relative w-full h-full">
              {/* progress bars */}
              <div className="absolute top-2 left-2 right-2 z-30 flex gap-1">
                {(currentGroup.stories || []).map((_, i) => (
                  <div key={i} className="flex-1 h-0.5 bg-white/30 rounded overflow-hidden">
                    <div key={`${openViewer.groupIndex}-${openViewer.storyIndex}-${i}`} data-testid={`story-progress-${i}`} className={`h-full bg-white ${i < openViewer.storyIndex ? "w-full" : i === openViewer.storyIndex ? "w-full animate-story-progress" : "w-0"}`} style={i === openViewer.storyIndex && replying ? { animationPlayState: "paused" } : undefined} />
                  </div>
                ))}
              </div>
              {/* header */}
              <div className="absolute top-6 left-2 right-2 z-30 flex items-center gap-2 mt-2 px-1">
                <Avatar className="w-8 h-8 border border-white/40">
                  <AvatarImage src={currentGroup.author_picture} />
                  <AvatarFallback>{currentGroup.author_name?.[0]}</AvatarFallback>
                </Avatar>
                <div className="text-white text-xs font-semibold flex-1 truncate">{currentGroup.author_name}</div>
                {isOwn && (
                  <>
                    <button data-testid="story-viewers-btn" onClick={() => openViewersFor(current.story_id)} className="text-white/80 hover:text-white text-[11px] flex items-center gap-1">
                      <Eye className="w-3.5 h-3.5" /> {current.views_count || 0}
                      {current.reactions_count > 0 && <span data-testid="story-reactions-count" className="ml-1">· {current.reactions?.slice(-3).map((r) => r.emoji).join("")} {current.reactions_count}</span>}
                    </button>
                    <button data-testid="story-delete-btn" onClick={() => deleteStory(current.story_id)} className="text-white/80 hover:text-red-400">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                )}
                <button onClick={() => setOpenViewer(null)} className="text-white/80 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              {/* content */}
              <div className="w-full h-full flex items-center justify-center" style={{ background: current.bg_color || "#111827" }}>
                {current.video_url ? (
                  <video key={current.story_id} src={current.video_url} controls autoPlay playsInline onEnded={nextStory} className="w-full h-full object-contain" data-testid="story-video" />
                ) : current.image_url ? (
                  <img src={current.image_url} alt="" className="w-full h-full object-contain" />
                ) : null}
                {current.text && !current.image_url && !current.video_url && (
                  <div className="text-white text-2xl font-bold text-center p-8 relative z-30" data-testid="story-text">{linkify(current.text, { dark: true, mentions: current.mentions })}</div>
                )}
                {current.text && (current.image_url || current.video_url) && (
                  <div className={`absolute ${current.link_url ? (isOwn ? "bottom-32" : "bottom-52") : (isOwn ? "bottom-8" : "bottom-28")} left-4 right-4 z-30 text-white text-lg font-bold text-center bg-black/40 backdrop-blur-sm rounded-xl p-3`} data-testid="story-text">{linkify(current.text, { dark: true, mentions: current.mentions })}</div>
                )}
                {current.link_url && <StoryLinkCard url={current.link_url} preview={current.link_preview} testid="story-link-card" className={isOwn ? "bottom-6" : "bottom-28"} />}
                {(current.music || current.location) && (
                  <div className="absolute top-14 left-3 right-3 z-30 flex flex-wrap gap-2" data-testid="story-tags">
                    <MusicTag music={current.music} testid="story-music-tag" />
                    <LocationTag location={current.location} testid="story-location-tag" />
                  </div>
                )}
                {current.music?.preview_url && !current.video_url && (
                  <audio key={current.story_id} src={current.music.preview_url} autoPlay loop data-testid="story-music-audio" />
                )}
              </div>
              {/* nav */}
              <button className="absolute left-0 top-0 bottom-0 w-1/3 z-20" onClick={prevStory} aria-label="Anterior" />
              <button className="absolute right-0 top-0 bottom-0 w-1/3 z-20" onClick={nextStory} aria-label="Próximo" />
              {!isOwn && <StoryReplyBar story={current} onFocusChange={setReplying} />}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Viewers list */}
      <Dialog open={!!openViewers} onOpenChange={(v) => !v && setOpenViewers(null)}>
        <DialogContent className="max-w-sm" data-testid="story-viewers-dialog">
          <div className="font-bold mb-2">Quem viu ({viewersList.length})</div>
          <div className="max-h-80 overflow-y-auto space-y-2">
            {viewersList.map((v, i) => (
              <div key={v.user_id || i} className="flex items-center gap-2 text-sm">
                <Avatar className="w-8 h-8"><AvatarImage src={v.picture} /><AvatarFallback>{v.name?.[0]}</AvatarFallback></Avatar>
                <div className="flex-1">{v.name}</div>
                <div className="text-[10px] text-muted-foreground">{v.at?.slice(11, 16)}</div>
              </div>
            ))}
            {viewersList.length === 0 && <div className="text-xs text-muted-foreground text-center py-4">Ninguém viu ainda.</div>}
          </div>
        </DialogContent>
      </Dialog>

      <style>{`
        @keyframes storyProgress { from { width: 0% } to { width: 100% } }
        @keyframes storyFly { 0% { transform: translate(-50%, 0) scale(.6); opacity: 0 } 20% { opacity: 1; transform: translate(-50%, -20px) scale(1.2) } 100% { transform: translate(-50%, -160px) scale(1); opacity: 0 } }
        .animate-story-fly { animation: storyFly 1.2s ease-out forwards; }
        .animate-story-progress { animation: storyProgress 6s linear forwards; }
        .scrollbar-none::-webkit-scrollbar { display: none; }
      `}</style>
    </>
  );
}

function StoryLinkCard({ url, preview, testid = "story-link-card", className = "bottom-6" }) {
  let host = preview?.source;
  try { host = host || new URL(url).hostname; } catch { host = host || url; }
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" data-testid={testid} onClick={(e) => e.stopPropagation()}
      className={`absolute ${className} left-4 right-4 z-40 flex items-center gap-3 rounded-2xl bg-white/95 text-slate-900 shadow-xl p-2 pr-3 hover:bg-white transition-colors`}>
      {preview?.image ? (
        <img src={preview.image} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0" />
      ) : (
        <div className="w-14 h-14 rounded-xl bg-slate-200 flex items-center justify-center shrink-0"><Link2 className="w-6 h-6 text-slate-500" /></div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 truncate">{host}</div>
        <div className="text-sm font-bold leading-snug line-clamp-2" data-testid={`${testid}-title`}>{preview?.title || url}</div>
      </div>
      <span className="shrink-0 rounded-full bg-slate-900 text-white text-xs font-bold px-3 py-1.5 flex items-center gap-1" data-testid={`${testid}-open`}>Abrir <ExternalLink className="w-3 h-3" /></span>
    </a>
  );
}

function StoryComposer({ open, onOpenChange, onCreated }) {
  const [text, setText] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [bg, setBg] = useState(BG_PALETTE[0]);
  const [music, setMusic] = useState(null);
  const [location, setLocation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [showLink, setShowLink] = useState(false);
  const [picked, setPicked] = useState([]);
  const textRef = useRef(null);
  const linkPrev = useLinkPreview(linkUrl, { delay: 500 });

  const submit = async () => {
    if (!text && !imageUrl && !videoUrl) return toast.error("Adicione texto, imagem ou vídeo");
    setBusy(true);
    try {
      const mention_ids = picked.filter((u) => text.toLowerCase().includes(`@${(u.name || "").split(" ")[0].toLowerCase()}`)).map((u) => u.user_id);
      await axios.post(`${API}/stories`, { text, image_url: imageUrl, video_url: videoUrl, bg_color: bg, music, location,
        link_url: linkPrev.url || null, link_preview: linkPrev.preview || null, mention_ids });
      toast.success("Story publicado! (24h)");
      setText(""); setImageUrl(""); setVideoUrl(""); setBg(BG_PALETTE[0]); setMusic(null); setLocation(null);
      setLinkUrl(""); setShowLink(false); setPicked([]); linkPrev.reset();
      onCreated?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="story-composer">
        <div className="font-bold text-lg">Novo story <span className="text-xs text-muted-foreground">(dura 24h)</span></div>
        <div className="rounded-xl aspect-[9/16] max-h-[420px] relative overflow-hidden mt-2" style={{ background: bg }}>
          {imageUrl && <img src={imageUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />}
          {videoUrl && !imageUrl && <video src={videoUrl} muted className="absolute inset-0 w-full h-full object-cover" />}
          {text && (
            <div className={`absolute inset-0 flex items-center justify-center text-white font-bold text-center p-6 ${imageUrl || videoUrl ? "bg-black/40" : ""}`}>
              <span className="text-2xl leading-tight drop-shadow">{text}</span>
            </div>
          )}
          {linkPrev.url && <StoryLinkCard url={linkPrev.url} preview={linkPrev.preview} testid="story-composer-link-card" />}
        </div>

        <div className="relative">
          <Textarea ref={textRef} data-testid="story-text-input" placeholder="Escreva algo... use @ para marcar alguém" value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={280} className="mt-2" />
          <MentionPicker value={text} onChange={setText} inputRef={textRef} onPick={(u) => setPicked((p) => (p.some((x) => x.user_id === u.user_id) ? p : [...p, u]))} />
        </div>
        {showLink ? (
          <div className="mt-2 space-y-2" data-testid="story-link-section">
            <Input data-testid="story-link-input" type="url" placeholder="Cole o link (https://...)" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} />
            {(linkPrev.preview || linkPrev.loading) && (
              <LinkPreviewCard preview={linkPrev.preview} loading={linkPrev.loading} compact testid="story-link-preview" />
            )}
            {linkUrl && !linkPrev.loading && !linkPrev.preview && linkPrev.url && <div className="text-[11px] text-muted-foreground">Sem prévia disponível — o botão "Abrir" ainda vai funcionar.</div>}
          </div>
        ) : null}

        <div className="flex gap-2 flex-wrap mt-1">
          {BG_PALETTE.map((c) => (
            <button
              key={c}
              data-testid={`story-bg-${c}`}
              onClick={() => setBg(c)}
              className={`w-7 h-7 rounded-full border-2 ${bg === c ? "border-primary" : "border-transparent"}`}
              style={{ background: c }}
            />
          ))}
        </div>

        <div className="flex gap-2 mt-2">
          <ImageUploader testid="story-image-upload" label="Imagem" variant="outline" size="sm" accept="image/*" onUploaded={(url) => setImageUrl(url)} />
          <ImageUploader testid="story-video-upload" label="Vídeo" variant="outline" size="sm" accept="video/*" onUploaded={(url) => setVideoUrl(url)} />
          {(imageUrl || videoUrl) && (
            <Button size="sm" variant="ghost" onClick={() => { setImageUrl(""); setVideoUrl(""); }} data-testid="story-clear-media">Limpar</Button>
          )}
          <Button size="sm" variant={showLink ? "secondary" : "outline"} data-testid="story-link-btn" onClick={() => { setShowLink((v) => !v); if (showLink) { setLinkUrl(""); linkPrev.reset(); } }} className="gap-1">
            <Link2 className="w-4 h-4" /> {showLink ? "Remover link" : "Link"}
          </Button>
        </div>

        <div className="flex flex-wrap gap-3 pt-1" data-testid="story-extras">
          <MusicPicker value={music} onChange={setMusic} />
          <LocationPicker value={location} onChange={setLocation} />
        </div>
        <Button data-testid="story-submit" onClick={submit} disabled={busy} className="w-full rounded-full mt-2 gap-2">
          <Send className="w-4 h-4" /> {busy ? "Publicando..." : "Publicar story"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
